import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { GraphContext, type ScopeDataParams } from '@ardoq/graph';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { workspaceUtils } from '@ardoq/scope-data';

/**
 * This is a temporary solution to make the transition from BB models to scope data easier.
 *
 * The next step is to create an object with functions that have the same signature as the model interface functions,
 * but are bound to the current scope data. This way we can use the scopeData or BB models interchangeably with minimal
 * refactoring. As soon BB models are no more, we shall pass the scope data instead of the interface object and use the
 * utility functions directly.
 *
 */

export type TimelineSpecificInterface = ReturnType<
  typeof getTimelineSpecificInterfaceWithModelInterfaces
>;

export const getTimelineSpecificInterfaceWithModelInterfaces = () => ({
  getAllComponents: componentInterface.getAllComponents,
  getComponentTypesForWorkspace: workspaceInterface.getComponentTypes,
  getWorkspaceNameById: workspaceInterface.getWorkspaceName,
  getAllGroupingRules: groupingRuleInterface.getAll,
});

const getTimelineSpecificInterfaceForEnhancedScopeData = ({
  enhancedScopeData,
  groupingRules,
}: Pick<
  GraphContext,
  'enhancedScopeData' | 'groupingRules'
>): TimelineSpecificInterface => ({
  getAllComponents: () => enhancedScopeData.components,
  getComponentTypesForWorkspace: workspaceId =>
    workspaceUtils.getAPIComponentTypes(enhancedScopeData, workspaceId),
  getWorkspaceNameById: workspaceId =>
    enhancedScopeData.workspacesById[workspaceId]?.name ?? null,

  getAllGroupingRules: () => groupingRules, // this is stupid, but it's about the shared pattern?
});

export const getTimelineSpecificInterface = (
  args?: ScopeDataParams
): TimelineSpecificInterface => {
  if (args) return getTimelineSpecificInterfaceForEnhancedScopeData(args);
  return getTimelineSpecificInterfaceWithModelInterfaces();
};
